/**
 * KAN-755: чистые функции раздела "Скидки": значения формы скидки по реестру
 * типов (параметры по типу поля), клиентская проверка, тело запроса, разбор
 * ошибок сервера по полям (включая params.*), статус и скоуп для таблицы.
 */
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import type { CatalogId } from '@/types/catalog';
import type {
  DiscountBody,
  DiscountDetail,
  DiscountListItem,
  DiscountParamSpec,
  DiscountParams,
  DiscountProductRef,
  DiscountState,
  DiscountType,
  ProductGroup,
  ProductGroupBody,
} from '@/types/discount';

/** Строка порога в форме: поля как ввёл пользователь. */
export interface TierRow {
  minAmount: string;
  percent: string;
}

/** Значение параметра в форме: строка поля или пороги. */
export type ParamValue = string | TierRow[];

export interface DiscountFormValues {
  name: string;
  formula: string;
  params: Record<string, ParamValue>;
  isActive: boolean;
  appliesToAll: boolean;
  products: DiscountProductRef[];
  categoryIds: string[];
  groupIds: string[];
}

/** Скоуп скидки в форме. */
export type ScopeValues = Pick<DiscountFormValues, 'appliesToAll' | 'products' | 'categoryIds' | 'groupIds'>;

/**
 * Ошибки формы: name, formula, scope, params (общая по параметрам),
 * params.<имя параметра>.
 */
export type FormErrors = Record<string, string>;

export const emptyTier = (): TierRow => ({ minAmount: '', percent: '' });

export function emptyParamValue(spec: Pick<DiscountParamSpec, 'type'>): ParamValue {
  return spec.type === 'tiers' ? [emptyTier()] : '';
}

export function emptyParams(type: DiscountType | undefined): Record<string, ParamValue> {
  const out: Record<string, ParamValue> = {};
  (type?.params ?? []).forEach((spec) => {
    out[spec.name] = emptyParamValue(spec);
  });
  return out;
}

export function emptyDiscountForm(formula = ''): DiscountFormValues {
  return {
    name: '',
    formula,
    params: {},
    isActive: true,
    appliesToAll: false,
    products: [],
    categoryIds: [],
    groupIds: [],
  };
}

const str = (value: unknown): string => (value === null || value === undefined ? '' : String(value));

function tiersToForm(value: unknown): TierRow[] {
  if (!Array.isArray(value) || value.length === 0) return [emptyTier()];
  return value.map((tier) => {
    const row = (tier ?? {}) as { min_amount?: unknown; percent?: unknown };
    return { minAmount: str(row.min_amount), percent: str(row.percent) };
  });
}

/** Параметр из API в значение поля формы по типу поля. */
export function paramToForm(spec: Pick<DiscountParamSpec, 'type'>, value: unknown): ParamValue {
  if (spec.type === 'tiers') return tiersToForm(value);
  if (spec.type === 'date') return str(value).slice(0, 10);
  return str(value);
}

export function discountToForm(discount: DiscountDetail, type: DiscountType | undefined): DiscountFormValues {
  const params: Record<string, ParamValue> = {};
  (type?.params ?? []).forEach((spec) => {
    params[spec.name] = paramToForm(spec, discount.params?.[spec.name]);
  });
  return {
    name: discount.name,
    formula: discount.formula,
    params,
    isActive: discount.is_active,
    appliesToAll: discount.applies_to_all,
    products: discount.products ?? [],
    categoryIds: (discount.categories ?? []).map((c) => String(c.id)),
    groupIds: (discount.groups ?? []).map((g) => String(g.id)),
  };
}

const NUMBER = /^\d+(\.\d{1,2})?$/;

/** "1 500,5" -> 1500.5; пусто -> null; неверно -> undefined. */
export function parseDecimal(raw: string): number | null | undefined {
  const value = raw.replace(/\s/g, '').replace(',', '.');
  if (value === '') return null;
  return NUMBER.test(value) ? Number(value) : undefined;
}

export const PERCENT_ERROR = 'Процент: число больше 0 и не больше 100';
export const MONEY_ERROR = 'Сумма: число не меньше 0';
export const REQUIRED_ERROR = 'Заполните поле';
export const SCOPE_ERROR = 'Выберите товары, категории или группы либо весь каталог';

function percentValue(raw: string): number | null | undefined {
  const value = parseDecimal(raw);
  if (value === null || value === undefined) return value;
  return value > 0 && value <= 100 ? value : undefined;
}

interface ParamResult {
  value?: unknown;
  error?: string;
}

function tiersParam(rows: TierRow[], required: boolean): ParamResult {
  const filled = rows.filter((row) => row.minAmount.trim() !== '' || row.percent.trim() !== '');
  if (filled.length === 0) return required ? { error: 'Добавьте хотя бы один порог' } : { value: [] };
  const tiers: { min_amount: number; percent: number }[] = [];
  for (const row of filled) {
    const minAmount = parseDecimal(row.minAmount);
    const percent = percentValue(row.percent);
    if (minAmount === null || minAmount === undefined) return { error: `Порог: ${MONEY_ERROR.toLowerCase()}` };
    if (percent === null || percent === undefined) return { error: `Порог: ${PERCENT_ERROR.toLowerCase()}` };
    tiers.push({ min_amount: minAmount, percent });
  }
  return { value: tiers };
}

/** Значение поля -> значение параметра для API или ошибка поля. */
export function paramFromForm(spec: DiscountParamSpec, raw: ParamValue | undefined): ParamResult {
  if (spec.type === 'tiers') return tiersParam(Array.isArray(raw) ? raw : [], spec.required);
  const text = typeof raw === 'string' ? raw.trim() : '';
  if (text === '') return spec.required ? { error: REQUIRED_ERROR } : {};
  switch (spec.type) {
    case 'percent': {
      const value = percentValue(text);
      return value === undefined || value === null ? { error: PERCENT_ERROR } : { value };
    }
    case 'money': {
      const value = parseDecimal(text);
      return value === undefined || value === null ? { error: MONEY_ERROR } : { value };
    }
    case 'string':
      return { value: text.toUpperCase() };
    default:
      return { value: text };
  }
}

export interface DiscountFormResult {
  body: DiscountBody | null;
  errors: FormErrors;
}

export function validateDiscountForm(values: DiscountFormValues, type: DiscountType | undefined): DiscountFormResult {
  const errors: FormErrors = {};
  const name = values.name.trim();
  if (!name) errors.name = 'Укажите название';
  if (!type) errors.formula = 'Выберите тип скидки';

  const params: DiscountParams = {};
  (type?.params ?? []).forEach((spec) => {
    const result = paramFromForm(spec, values.params[spec.name]);
    if (result.error) errors[`params.${spec.name}`] = result.error;
    else if (result.value !== undefined) params[spec.name] = result.value;
  });

  const hasScope = values.products.length + values.categoryIds.length + values.groupIds.length > 0;
  if (!values.appliesToAll && !hasScope) errors.scope = SCOPE_ERROR;

  if (Object.keys(errors).length > 0) return { body: null, errors };
  return {
    errors,
    body: {
      name,
      formula: values.formula,
      params,
      is_active: values.isActive,
      applies_to_all: values.appliesToAll,
      product_ids: values.appliesToAll ? [] : values.products.map((p) => p.id),
      category_ids: values.appliesToAll ? [] : values.categoryIds,
      group_ids: values.appliesToAll ? [] : values.groupIds,
    },
  };
}

/** Первое непустое сообщение в значении ошибки любой вложенности. */
export function firstMessage(value: unknown): string | null {
  if (typeof value === 'string') return value || null;
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = firstMessage(item);
      if (found) return found;
    }
    return null;
  }
  if (value && typeof value === 'object') {
    for (const item of Object.values(value as Record<string, unknown>)) {
      const found = firstMessage(item);
      if (found) return found;
    }
  }
  return null;
}

export interface ServerErrors {
  fields: FormErrors;
  form: string | null;
}

const DISCOUNT_FIELDS: Record<string, string> = {
  name: 'name',
  formula: 'formula',
  applies_to_all: 'scope',
  product_ids: 'scope',
  category_ids: 'scope',
  group_ids: 'scope',
  scope: 'scope',
};

const GROUP_FIELDS: Record<string, string> = {
  name: 'name',
  description: 'description',
  product_ids: 'products',
};

function mapServerErrors(err: unknown, fieldMap: Record<string, string>, fallback: string): ServerErrors {
  const data = (err as { response?: { data?: unknown } } | null)?.response?.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return { fields: {}, form: extractErrorMessage(err, fallback) };
  }
  const fields: FormErrors = {};
  const rest: string[] = [];
  Object.entries(data as Record<string, unknown>).forEach(([key, value]) => {
    if (key === 'code') return;
    if (key === 'params' && value && typeof value === 'object' && !Array.isArray(value)) {
      Object.entries(value as Record<string, unknown>).forEach(([param, inner]) => {
        const message = firstMessage(inner);
        if (message) fields[`params.${param}`] = message;
      });
      return;
    }
    const message = firstMessage(value);
    if (!message) return;
    const field = key === 'params' ? 'params' : fieldMap[key];
    if (field && !fields[field]) fields[field] = message;
    else if (!field) rest.push(message);
  });
  const form = rest.length > 0 ? rest.join(' ') : null;
  if (Object.keys(fields).length === 0 && !form) return { fields, form: extractErrorMessage(err, fallback) };
  return { fields, form };
}

/** Ответ 400 по скидке: поля, params.* и общая ошибка. */
export function serverDiscountErrors(err: unknown): ServerErrors {
  return mapServerErrors(err, DISCOUNT_FIELDS, 'Не удалось сохранить скидку');
}

export function serverGroupErrors(err: unknown): ServerErrors {
  return mapServerErrors(err, GROUP_FIELDS, 'Не удалось сохранить группу');
}

export interface GroupFormValues {
  name: string;
  description: string;
  products: DiscountProductRef[];
}

export function groupToForm(group: ProductGroup | null | undefined): GroupFormValues {
  return {
    name: group?.name ?? '',
    description: group?.description ?? '',
    products: group?.products ?? [],
  };
}

export function validateGroupForm(values: GroupFormValues): { body: ProductGroupBody | null; errors: FormErrors } {
  const name = values.name.trim();
  if (!name) return { body: null, errors: { name: 'Укажите название' } };
  return {
    errors: {},
    body: {
      name,
      description: values.description.trim(),
      product_ids: values.products.map((p) => p.id),
    },
  };
}

/** 409 при удалении группы из скидки: текст и названия скидок. */
export function groupDeleteErrorMessage(err: unknown): string {
  const data = (err as { response?: { data?: { discounts?: unknown } } } | null)?.response?.data;
  const base = extractErrorMessage(err, 'Не удалось удалить группу');
  const names = Array.isArray(data?.discounts)
    ? data.discounts.filter((name): name is string => typeof name === 'string' && name !== '')
    : [];
  return names.length > 0 ? `${base} Скидки: ${names.join(', ')}.` : base;
}

export interface StateView {
  label: string;
  variant: 'completed' | 'pending' | 'secondary' | 'outline';
}

export const DISCOUNT_STATE_VIEW: Record<DiscountState, StateView> = {
  active: { label: 'Действует', variant: 'completed' },
  scheduled: { label: 'Запланирована', variant: 'pending' },
  expired: { label: 'Истекла', variant: 'secondary' },
  disabled: { label: 'Выключена', variant: 'outline' },
};

export const DISCOUNT_STATES: readonly DiscountState[] = ['active', 'scheduled', 'expired', 'disabled'];

/** Статус скидки; выключенная всегда "Выключена". */
export function discountStateView(item: Pick<DiscountListItem, 'state' | 'is_active'>): StateView {
  if (!item.is_active) return DISCOUNT_STATE_VIEW.disabled;
  return DISCOUNT_STATE_VIEW[item.state] ?? { label: String(item.state), variant: 'secondary' };
}

/** "Весь каталог" или "Товары: 2, категории: 1, группы: 1". */
export function discountScopeText(item: Pick<DiscountListItem, 'applies_to_all' | 'scope_counts'>): string {
  if (item.applies_to_all) return 'Весь каталог';
  const counts = item.scope_counts ?? { products: 0, categories: 0, groups: 0 };
  const parts = [
    counts.products ? `товары: ${counts.products}` : null,
    counts.categories ? `категории: ${counts.categories}` : null,
    counts.groups ? `группы: ${counts.groups}` : null,
  ].filter((part): part is string => part !== null);
  if (parts.length === 0) return '-';
  const text = parts.join(', ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Добавить или убрать id в списке выбранных. */
export function toggleId(ids: readonly string[], id: CatalogId): string[] {
  const key = String(id);
  return ids.includes(key) ? ids.filter((x) => x !== key) : [...ids, key];
}
